<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue'
import Cookies from 'js-cookie'
import { CustomActivityTracker } from '@/helpers/trackers'
import { useStudentScreen } from './useStudentScreen'
import StudentGamePurchaseModal from '@/ui-kit/student/GamePurchaseModal.vue'
import GameKaraoke from '@/games/GameKaraoke.vue'
import GameShadowing from '@/games/GameShadowing.vue'
import type { Game, Student2Context } from '@/types/student2'
import type { PaymentModalExpose } from '@/ui-kit/components/PaymentModal/PaymentModal.vue'

const props = defineProps<{ context: Student2Context }>()
const emit = defineEmits<{
  (e: 'close'): void
}>()

const { isMobileLocal } = useStudentScreen()

const unitId = ref(Number(window.location.pathname.split('/').pop()))
const activeGame = ref<Game | null>(null)
const orderGame = ref<Game | null>(null)
const canPingGameActivity = ref(false)

const paymentModal = useTemplateRef<PaymentModalExpose>('paymentModal')
const gameTrackers: Record<number, CustomActivityTracker> = {}

const isActiveGameIntegrated = computed(() => {
  const integratedGames = ['shadowing', 'karaoke', 'videoteacher', 'irregularverbs']
  return !!activeGame.value && integratedGames.includes(activeGame.value.name)
})

watch(orderGame, val => {
  if (!val && !activeGame.value) emit('close')
})

watch(activeGame, (newValue, oldValue) => {
  if (!newValue && !orderGame.value) emit('close')

  if (newValue === oldValue) return
  if (newValue) onGamePlay(newValue.id)
  if (oldValue) onGamePause(oldValue.id)
})

const $openGame = (game: Game) => {
  if (game.unlocked) {
    activeGame.value = game
  } else {
    orderGame.value = game
  }
}

const confirmPurchase = (selectedUnitId?: number, isSmartBook = false) => {
  if (!orderGame.value) return

  const query: Record<string, string | number> = {}
  const game = { ...orderGame.value }
  let name = `${game.friendly_name}`

  if (isSmartBook) {
    query.course_id = props.context.course_part.course_id
    query.student_ids = props.context.student.id
  } else {
    query.course_part_id = props.context.course_part.id
    query.student_id = props.context.student.id
    query.game_id = game.id
  }

  let price = 0
  if (selectedUnitId) {
    query.unit_id = selectedUnitId
    const unit = props.context.course_part.units.find(e => e.id === selectedUnitId)
    if (!unit) return

    name = `${game.friendly_name} - ${unit.title}`
    price = game.price?.unit_price ?? 0
  } else {
    price = game.price?.course_part_price ?? 0
  }

  orderGame.value = null
  paymentModal.value?.open({
    url: isSmartBook ? '/parent/request_purchase_smartbook' : '/student/request_purchase_game',
    order: {
      name: `${name} (доступ на 18 месяцев)`,
      price
    },
    query
  })
}

const onGamePlay = (materialId: number) => {
  if (!gameTrackers[materialId]) {
    const isPlayingFunc = () => activeGame.value?.id === materialId && !document.hidden
    const trackFunc = (ms: number) => {
      const form = new FormData()
      form.append('csrfmiddlewaretoken', Cookies.get('csrftoken') || '')
      form.append('student_id', String(props.context.student.id))
      form.append('unit_id', String(unitId.value))
      form.append('game_id', String(materialId))
      form.append('milliseconds', String(ms))
      navigator.sendBeacon('/student/track_game_activity', form)
    }

    gameTrackers[materialId] = new CustomActivityTracker(isPlayingFunc, trackFunc)
  }

  gameTrackers[materialId].startTracking()
}

const onGamePause = (materialId: number) => {
  if (gameTrackers[materialId]) {
    gameTrackers[materialId].stopTracking()
  }
}

defineExpose({ $openGame })
</script>

<template>
  <div>
    <v-payment-modal ref="paymentModal" :student-id="context.student.id" />

    <StudentGamePurchaseModal
      v-model="orderGame"
      :active-unit-id="unitId"
      :units="context.course_part.units"
      :course-part="context.course_part"
      @confirm="confirmPurchase"
    />

    <v-modal
      :model-value="!!activeGame"
      :mode="isActiveGameIntegrated ? 'dark' : ''"
      class="game-modal"
      :class="{ 'game-modal_mobile': isMobileLocal }"
      @update:model-value="activeGame = null"
    >
      <template #header>
        <div class="game-modal__header">
          <h3 v-if="!isActiveGameIntegrated" class="game-modal__title text-h3">
            {{ activeGame?.friendly_name }}
          </h3>
        </div>
      </template>
      <div v-if="activeGame" class="game-modal__content">
        <template v-if="isActiveGameIntegrated">
          <GameKaraoke
            v-if="activeGame.name === 'karaoke'"
            :game="activeGame"
            :unit-id="unitId"
            :student-id="context.student.id"
            :course-name="context.course_part.title"
            :force-desktop="isMobileLocal"
            @play="canPingGameActivity = true"
            @pause="canPingGameActivity = false"
          />
          <GameShadowing
            v-else
            :game="activeGame"
            :unit-id="unitId"
            :student-id="context.student.id"
            @play="canPingGameActivity = true"
            @pause="canPingGameActivity = false"
          />
        </template>
        <iframe v-else :src="`${activeGame.url || ''}&frame=1`" frameborder="0" width="100%" height="100%" />
      </div>
    </v-modal>
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/student2/screen' as s2;

main.student-cabinet-v2 {
  .game-modal__content {
    @media (orientation: portrait) {
      width: calc(100vh);
      height: calc(100vw - #{8 * $grid-gap});
    }

    @include s2.mobile {
      height: calc(100vh - #{8 * $grid-gap});
      width: calc(100vw);
    }
  }
}

.student-games-page {
  padding: 0 max(24px, calc((100% - 920px) / 2));
  padding-top: calc(var(--header_height, 0) + 24px);
  padding-bottom: 64px;

  &__placeholder {
    height: calc(100% - 64px);
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 24px;
    @include text-title;
    flex-direction: column;
  }

  .games {
    display: grid;
    grid-gap: 2 * $grid-gap;

    grid-template-columns: repeat(auto-fill, 28 * $grid-gap);
    row-gap: 4 * $grid-gap;

    &__student-book {
      order: 4;
      min-width: 256px;

      .poster {
        height: 162px;
      }
    }

    &__title {
      @include text-title;
      font-weight: bold;
    }
  }
}

.game-modal {
  &__header {
    display: flex;
    align-items: center;
    height: 9 * $grid-gap;
    border-bottom-color: $darkGray;
    padding: 3 * $grid-gap 4 * $grid-gap 2 * $grid-gap;
  }

  &__content {
    width: calc(100vw - #{4 * $grid-gap});
    height: calc(100vh - #{13 * $grid-gap});
  }
}

.v-modal.game-modal {
  z-index: 1002;

  .v-modal__container {
    max-height: inherit;
    overflow: hidden;
    border-radius: 0;
    width: 100%;
    height: 100%;
  }

  .v-modal__content {
    margin: 0;
    padding: 0;
    max-height: inherit;
  }

  .v-modal__close {
    top: 3 * $grid-gap;
    right: 3 * $grid-gap;
    width: 4 * $grid-gap;
    height: 4 * $grid-gap;
  }
}
</style>
